import React, { FC, memo, useCallback, useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import AnimatedIcon from '../../common/AnimatedIcon';
import { LOCAL_TGS_URLS } from '../../common/helpers/animatedAssets';
import Button from '../../ui/Button';
import IconSvg from '../../ui/IconSvg';
import { copyTextToClipboard } from '../../../util/clipboard';
import { getActions, withGlobal } from '../../../global';
import { ApiChat, ApiExportedInvite, ApiUser } from '../../../api/types';
import {
  selectChat,
  selectTabState,
  selectUser,
} from '../../../global/selectors';
import { isUserId } from '../../../global/helpers';
import { APP_LINK_PREFIX } from '../../../config';
import InviteQRCodeModal from '../../common/InviteQRCodeModal';
import useFlag from '../../../hooks/useFlag';

type OwnProps = {
  currentUserId?: string;
  onSelectFeed: () => void;
  onClickInvite: () => void;
};

type StateProps = {
  user?: ApiUser;
  chat?: ApiChat;
  exportedInvites?: ApiExportedInvite[];
};

const WelcomeEllo: FC<OwnProps & StateProps> = ({
  user,
  exportedInvites,
  onSelectFeed,
  onClickInvite,
}) => {
  const { showNotification, openChatWithShare } = getActions();
  const { t } = useTranslation();

  const [isOpenQRCodeModal, openQRCodeModal, closeQRCodeModal] = useFlag();

  function copy(text: string, entity: string) {
    copyTextToClipboard(text);
    showNotification({ message: `${entity} ${t('Copy.WasCopied')}` });
  }
  const { usernames, isSelf, isPublic } = user || {};
  const activeUsernames = useMemo(() => {
    const result = usernames?.filter((u) => u.isActive);
    return result?.length ? result : undefined;
  }, [usernames]);

  const invite =
    isSelf &&
    !isPublic &&
    exportedInvites?.find((el) => !el.usageLimit && !el.expireDate)?.link;

  const inviteLink =
    isSelf && !isPublic
      ? invite
      : activeUsernames && `${APP_LINK_PREFIX}${activeUsernames[0].username}`;

  const handleShare = useCallback(() => {
    if (inviteLink) {
      openChatWithShare({ link: inviteLink });
    }
  }, [inviteLink]);

  return (
    <div className='welcome-swiper'>
      {isPublic ? (
        <>
          <AnimatedIcon tgsUrl={LOCAL_TGS_URLS.Invite} size={130} />
          <div className='link-description'>
            Share the link or QR code to invite friends to join Ello and start
            chatting with you.
          </div>
          <div className='link-item-wrap'>
            <div className='link-item'>
              <div
                className='invite-link'
                role='button'
                onClick={() => copy(String(inviteLink), 'Link')}
              >
                <span className='link'>{inviteLink}</span>
              </div>

              <div className='ChatExtraActions'>
                <Button
                  round
                  color='translucent'
                  className='activated'
                  onClick={openQRCodeModal}
                  ariaLabel='Invite by QR Code'
                >
                  <i className='icon-svg'>
                    <IconSvg name='qr' />
                  </i>
                </Button>
              </div>
            </div>
            <div className='link-item'>
              <div
                className='invite-link text-center'
                role='button'
                onClick={handleShare}
              >
                <span className='title link'>Share invite link</span>
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className='private-invite-wrap'>
            <AnimatedIcon tgsUrl={LOCAL_TGS_URLS.PrivateChannel} size={156} />
            <h4>Private Account Invites</h4>
            <div className='link-description'>
              Invite trusted people to connect with you.
            </div>
            <Button round onClick={onClickInvite}>
              <IconSvg name='arrow-right' w='32' h='32' />
            </Button>
          </div>
          <div className='divider' />
        </>
      )}

      <div className='link-item'>
        <div
          className='invite-link text-center'
          role='button'
          onClick={onSelectFeed}
        >
          <span className='title link'>Discover channels</span>
        </div>
      </div>

      <InviteQRCodeModal
        isOpen={isOpenQRCodeModal}
        user={user}
        onClose={closeQRCodeModal}
        link={inviteLink}
        text={`@${activeUsernames?.[0].username}`}
      />
    </div>
  );
};
export default memo(
  withGlobal<OwnProps>((global, { currentUserId }): StateProps => {
    const { management } = selectTabState(global);
    const chat = currentUserId ? selectChat(global, currentUserId) : undefined;
    const { invites } = management.byChatId[chat?.id!] || {};
    const user = isUserId(currentUserId!)
      ? selectUser(global, currentUserId!)
      : undefined;

    return {
      chat,
      user,
      exportedInvites: invites,
    };
  })(WelcomeEllo)
);
